import { ContactOption } from './types';

interface ContinEmailBodyFields {
  dueBy?: string | null;
  isPriority?: boolean | null;
  requestNotes?: string | null;
}

interface ContinEmailSubjectDefaultFields {
  titleNumber: string;
  isAbstract: boolean;
  municipality?: string;
}

export const buildContinEmailSubjectDefault = ({
  titleNumber,
  isAbstract,
  municipality,
}: ContinEmailSubjectDefaultFields) => {
  const addOn = isAbstract
    ? 'Contin Request'
    : municipality
    ? `Municipal Request - ${municipality}`
    : 'Municipal Request';

  return `${titleNumber} - ${addOn}`;
};

export const buildContinEmailContactOptions = (email?: string) =>
  email ? [{ value: email, label: email }] : [];

export const hasVendorEmail = (vendorEmailsById: Record<number, string>, vendorId: number) =>
  Object.prototype.hasOwnProperty.call(vendorEmailsById, vendorId);

export const getVendorEmail = (vendorEmailsById: Record<number, string>, vendorId?: number) =>
  vendorId && hasVendorEmail(vendorEmailsById, vendorId) ? vendorEmailsById[vendorId] : '';

export const getVendorContactOptions = (vendorEmailsById: Record<number, string>, vendorId?: number) =>
  buildContinEmailContactOptions(getVendorEmail(vendorEmailsById, vendorId));

export const contactOptionsMatch = (first: ContactOption[] = [], second: ContactOption[] = []) =>
  first.length === second.length &&
  first.every(
    (contact, index) => contact.value === second[index]?.value && contact.label === second[index]?.label
  );

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatDateForEmail = (date: string) => {
  const dateParts = date.split('-');
  if (dateParts.length !== 3) {
    return escapeHtml(date);
  }

  return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
};

const isEmptyRichText = (value?: string | null) => {
  const trimmedValue = value?.trim();
  return !trimmedValue || trimmedValue.toLowerCase() === '<p><br></p>';
};

const hasHtmlTags = (value: string) => /<\s*\/?\s*[a-z][\s\S]*>/i.test(value);

const removeLeadingBlockMargin = (value: string) =>
  value.replace(/^<(p|div|ul|ol)([^>]*)>/i, (match, tagName, rawAttributes = '') => {
    const attributes = rawAttributes as string;
    const styleMatch = attributes.match(/\sstyle=(['"])(.*?)\1/i);

    if (!styleMatch) {
      return `<${tagName}${attributes} style="margin-top: 0;">`;
    }

    if (/margin-top\s*:/i.test(styleMatch[2])) {
      return match;
    }

    const styleValue = styleMatch[2].trim();
    const updatedStyleValue = `${styleValue}${styleValue.endsWith(';') ? '' : ';'} margin-top: 0;`;
    return `<${tagName}${attributes.replace(styleMatch[0], ` style=${styleMatch[1]}${updatedStyleValue}${styleMatch[1]}`)}>`;
  });

const formatNotesForEmail = (notes: string) => {
  const trimmedNotes = notes.trim();
  return hasHtmlTags(trimmedNotes)
    ? removeLeadingBlockMargin(trimmedNotes)
    : escapeHtml(trimmedNotes).replace(/\r?\n/g, '<br />');
};

export const buildContinEmailBody = ({
  dueBy,
  isPriority,
  requestNotes,
}: ContinEmailBodyFields) => {
  const rows: string[] = [];
  const notes = requestNotes ?? '';

  if (isPriority) {
    rows.push('<div><strong>**PRIORITY**</strong></div>');
  }

  if (dueBy) {
    rows.push(`<div>Due By: ${formatDateForEmail(dueBy)}</div>`);
  }

  if (!isEmptyRichText(notes)) {
    rows.push(`<div>Notes:</div>${formatNotesForEmail(notes)}`);
  }

  return rows.join('');
};
